import * as api from '@apis/integration';
import { IntegrationApiKey, IntegrationEndpoint } from '@apis/integration';
import { useAuthentication } from '@hooks/authentication';
import { API_URL } from '@variables';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button, Card, Checkbox, Form, Input, Modal, Table, Tag, Typography, message } from 'antd';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import React, { useMemo, useState } from 'react';

const { Paragraph, Text } = Typography;

const PREVIEW_ITEMS = 2;
const PREVIEW_CHARS = 6000;

const formatTime = (value?: string | null) => (value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '-');

const defaultQueryValue = (endpointId: string, name: string) => {
  if (endpointId === 'partner-sales' && name === 'startDate') {
    return dayjs().startOf('month').format('YYYY-MM-DD');
  }
  if (endpointId === 'partner-sales' && name === 'endDate') {
    return dayjs().add(1, 'day').format('YYYY-MM-DD');
  }
  if ((endpointId === 'partners' || endpointId === 'settlements') && name === 'page') {
    return '0';
  }
  if (endpointId === 'partners' && name === 'size') {
    return '2';
  }
  if (endpointId === 'settlements' && name === 'startDate') {
    return dayjs().startOf('month').format('YYYY-MM-DD');
  }
  if (endpointId === 'settlements' && name === 'endDate') {
    return dayjs().add(1, 'day').format('YYYY-MM-DD');
  }
  if (endpointId === 'settlements' && name === 'size') {
    return '2';
  }
  return '';
};

const applyPathParams = (path: string, values: Record<string, string>) =>
  path.replace(/\{([^}]+)\}/g, (_, name) => encodeURIComponent((values[name] || '').trim()));

const buildIntegrationUrl = (path: string, query: Record<string, string>) => {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    const trimmed = value.trim();
    if (trimmed) {
      params.set(key, trimmed);
    }
  });
  const search = params.toString();
  return `${API_URL}${path}${search ? `?${search}` : ''}`;
};

const buildCurl = (method: string, url: string, apiKey: string, body?: Record<string, string>) => {
  const key = (apiKey.trim() || 'igk_발급한키').replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  if (method === 'POST') {
    const payload = JSON.stringify(body || {}).replace(/'/g, `'\\''`);
    return `curl -sS -X POST -H "X-Api-Key: ${key}" -H "Content-Type: application/json" -d '${payload}' "${url}"`;
  }
  return `curl -sS -H "X-Api-Key: ${key}" "${url}"`;
};

const previewResponse = (data: unknown) => {
  const notes: string[] = [];
  let value = data;
  if (value && typeof value === 'object') {
    const copy = Array.isArray(value) ? [...value] : { ...(value as Record<string, unknown>) };
    ['rows', 'contents', 'items'].forEach((key) => {
      const list = (copy as Record<string, unknown>)[key];
      if (Array.isArray(list) && list.length > PREVIEW_ITEMS) {
        notes.push(`${key} ${list.length}건 중 ${PREVIEW_ITEMS}건`);
        (copy as Record<string, unknown>)[key] = list.slice(0, PREVIEW_ITEMS);
      }
    });
    value = copy;
  }

  let text = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
  if (text.length > PREVIEW_CHARS) {
    notes.push(`본문 ${text.length.toLocaleString()}자 중 ${PREVIEW_CHARS.toLocaleString()}자`);
    text = `${text.slice(0, PREVIEW_CHARS)}\n…`;
  }

  return {
    text,
    note: notes.length > 0 ? `${notes.join(', ')}만 표시합니다. curl로 호출하면 전체 응답을 받습니다.` : undefined,
  };
};

const EndpointTester: React.FC<{
  endpoint: IntegrationEndpoint;
  apiKey: string;
  onCalled: () => void;
}> = ({ endpoint, apiKey, onCalled }) => {
  const pathFields = endpoint.pathParams || [];
  const valueFields = endpoint.body?.length ? endpoint.body : endpoint.query;
  const fields = [...pathFields, ...valueFields];
  const [query, setQuery] = useState<Record<string, string>>(() =>
    Object.fromEntries(fields.map((field) => [field.name, defaultQueryValue(endpoint.id, field.name)])),
  );
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{ status: number; text: string; note?: string } | null>(null);
  const url = useMemo(() => {
    const path = applyPathParams(endpoint.path, query);
    if (endpoint.method === 'POST') {
      return `${API_URL}${path}`;
    }
    const queryOnly = Object.fromEntries(valueFields.map((field) => [field.name, query[field.name] || '']));
    return buildIntegrationUrl(path, queryOnly);
  }, [endpoint.method, endpoint.path, query, valueFields]);
  const curl = useMemo(() => {
    const body =
      endpoint.method === 'POST'
        ? Object.fromEntries(valueFields.map((field) => [field.name, query[field.name] || '']))
        : undefined;
    return buildCurl(endpoint.method, url, apiKey, body);
  }, [endpoint.method, url, apiKey, query, valueFields]);

  const callEndpoint = async () => {
    if (!apiKey.trim()) {
      message.warning('테스트할 API 키를 입력하세요.');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(url, {
        method: endpoint.method,
        headers: {
          Accept: 'application/json',
          'X-Api-Key': apiKey.trim(),
          ...(endpoint.method === 'POST' ? { 'Content-Type': 'application/json' } : {}),
        },
        body: endpoint.method === 'POST' ? JSON.stringify(query) : undefined,
      });
      const body = await response.text();
      let parsed: unknown = body;
      try {
        parsed = body ? JSON.parse(body) : null;
      } catch {
        parsed = body;
      }
      const preview = previewResponse(parsed);
      setResult({ status: response.status, ...preview });
      if (response.ok) {
        onCalled();
      }
    } catch (error) {
      setResult({
        status: 0,
        text: error instanceof Error ? error.message : '호출에 실패했습니다.',
        note: '브라우저에서 API 서버로 직접 요청했습니다. 네트워크 또는 CORS 오류일 수 있습니다.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
        <Text strong>{endpoint.title}</Text>
        <Tag>{endpoint.method}</Tag>
        <Text type="secondary">{endpoint.path}</Text>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
        {fields.map((field) => (
          <div key={field.name}>
            <div style={{ fontSize: 12, marginBottom: 4 }}>
              {field.name}
              {field.required ? ' *' : ''}
            </div>
            <Input
              value={query[field.name] || ''}
              placeholder={field.description}
              style={{ width: 280 }}
              onChange={(event) => setQuery((prev) => ({ ...prev, [field.name]: event.target.value }))}
            />
          </div>
        ))}
      </div>
      <Button type="primary" loading={loading} onClick={callEndpoint} style={{ marginBottom: 12 }}>
        호출
      </Button>
      <Paragraph style={{ marginBottom: 8 }}>
        <Text type="secondary">curl</Text>
      </Paragraph>
      <Paragraph copyable={{ text: curl }} code style={{ whiteSpace: 'pre-wrap' }}>
        {curl}
      </Paragraph>
      {result && (
        <div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
            <Tag color={result.status >= 200 && result.status < 300 ? 'green' : 'red'}>
              {result.status || '네트워크 오류'}
            </Tag>
            {result.note && <Text type="secondary">{result.note}</Text>}
          </div>
          <pre
            style={{
              maxHeight: 360,
              overflow: 'auto',
              margin: 0,
              padding: 12,
              background: '#f6f7f8',
              fontSize: 12,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}>
            {result.text}
          </pre>
        </div>
      )}
    </div>
  );
};

export const IntegrationApisPage: React.FC = () => {
  const [form] = Form.useForm<{ name: string; scopes: string[] }>();
  const [issuedKey, setIssuedKey] = useState<IntegrationApiKey | null>(null);
  const [testApiKey, setTestApiKey] = useState('');
  useAuthentication();

  const catalogQuery = useQuery({
    queryKey: ['integration-catalog'],
    queryFn: () => api.getIntegrationCatalog(),
  });
  const keysQuery = useQuery({
    queryKey: ['integration-api-keys'],
    queryFn: () => api.getIntegrationApiKeys(),
  });

  const rememberIssuedKey = (key?: string) => {
    if (key) {
      setTestApiKey(key);
    }
  };

  const endpoints = catalogQuery.data?.data.endpoints || [];
  const scopes = catalogQuery.data?.data.scopes || [];
  const keys = keysQuery.data?.data || [];

  const createMutation = useMutation({
    mutationFn: (values: { name: string; scopes: string[] }) => api.createIntegrationApiKey(values),
    onSuccess: (response) => {
      form.resetFields(['name']);
      setIssuedKey(response.data);
      rememberIssuedKey(response.data.apiKey);
      keysQuery.refetch();
    },
    onError: () => message.error('키 발급에 실패했습니다.'),
  });

  const rotateMutation = useMutation({
    mutationFn: (id: string) => api.rotateIntegrationApiKey(id),
    onSuccess: (response) => {
      setIssuedKey(response.data);
      rememberIssuedKey(response.data.apiKey);
      keysQuery.refetch();
    },
    onError: () => message.error('키 재발급에 실패했습니다.'),
  });

  const revokeMutation = useMutation({
    mutationFn: (id: string) => api.revokeIntegrationApiKey(id),
    onSuccess: () => {
      message.success('키를 폐기했습니다.');
      keysQuery.refetch();
    },
    onError: () => message.error('키 폐기에 실패했습니다.'),
  });

  React.useEffect(() => {
    if (scopes.length && !form.getFieldValue('scopes')?.length) {
      form.setFieldsValue({ scopes });
    }
  }, [scopes, form]);

  const endpointColumns: ColumnsType<IntegrationEndpoint> = [
    { title: '이름', dataIndex: 'title', width: 140 },
    { title: '메서드', dataIndex: 'method', width: 90 },
    {
      title: '주소',
      dataIndex: 'path',
      render: (path: string) => <Text copyable={{ text: `${API_URL}${path}` }}>{path}</Text>,
    },
    {
      title: '권한',
      dataIndex: 'scope',
      width: 140,
      render: (scope: string) => <Tag>{scope}</Tag>,
    },
    { title: '설명', dataIndex: 'description' },
  ];

  const keyColumns: ColumnsType<IntegrationApiKey> = [
    { title: '이름', dataIndex: 'name', width: 180 },
    { title: '접두어', dataIndex: 'keyPrefix', width: 140 },
    {
      title: '권한',
      dataIndex: 'scopes',
      render: (scopes: string[]) => scopes.map((scope) => <Tag key={scope}>{scope}</Tag>),
    },
    {
      title: '상태',
      dataIndex: 'status',
      width: 90,
      render: (status: IntegrationApiKey['status']) =>
        status === 'active' ? <Tag color="green">사용</Tag> : <Tag>폐기</Tag>,
    },
    { title: '마지막 사용', dataIndex: 'lastUsedAt', width: 160, render: formatTime },
    { title: '발급', dataIndex: 'createdAt', width: 160, render: formatTime },
    {
      title: '',
      width: 180,
      render: (_, record) =>
        record.status === 'active' ? (
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              size="small"
              loading={rotateMutation.isPending && rotateMutation.variables === record.id}
              onClick={() => {
                Modal.confirm({
                  title: '키를 재발급할까요?',
                  content: '기존 키는 바로 사용할 수 없게 됩니다.',
                  okText: '재발급',
                  cancelText: '취소',
                  onOk: () => rotateMutation.mutateAsync(record.id),
                });
              }}>
              재발급
            </Button>
            <Button
              size="small"
              danger
              loading={revokeMutation.isPending && revokeMutation.variables === record.id}
              onClick={() => {
                Modal.confirm({
                  title: '키를 폐기할까요?',
                  content: '폐기한 키는 다시 사용할 수 없습니다.',
                  okText: '폐기',
                  okButtonProps: { danger: true },
                  cancelText: '취소',
                  onOk: () => revokeMutation.mutateAsync(record.id),
                });
              }}>
              폐기
            </Button>
          </div>
        ) : (
          formatTime(record.revokedAt)
        ),
    },
  ];

  return (
    <div className="dashboard" style={{ position: 'relative' }}>
      <div className="page-header">
        <div>연동 API</div>
      </div>
      <div className="page-content" style={{ height: 'calc(100vh - 80px)', overflow: 'auto', padding: 16 }}>
        <Paragraph>
          읽기 전용 키로 아래 주소만 호출할 수 있습니다. 헤더는 <Text code>X-Api-Key</Text> 입니다. 키 원문은
          발급·재발급 순간에만 표시되고, 새로고침하면 테스트 칸에서도 사라집니다.
        </Paragraph>
        <Table
          rowKey="id"
          columns={endpointColumns}
          dataSource={endpoints}
          pagination={false}
          loading={catalogQuery.isLoading}
          style={{ marginBottom: 24 }}
        />
        <Form
          form={form}
          layout="inline"
          style={{ marginBottom: 16 }}
          onFinish={(values) => createMutation.mutate(values)}>
          <Form.Item name="name" rules={[{ required: true, message: '키 이름을 입력하세요.' }]}>
            <Input placeholder="키 이름 (예: 카카오 상담)" style={{ width: 280 }} />
          </Form.Item>
          <Form.Item name="scopes" rules={[{ required: true, message: '권한을 하나 이상 선택하세요.' }]}>
            <Checkbox.Group
              options={scopes.map((scope) => {
                const titles = endpoints.filter((item) => item.scope === scope).map((item) => item.title);
                return { label: titles.length ? `${titles.join(' · ')} (${scope})` : scope, value: scope };
              })}
            />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={createMutation.isPending}>
            키 발급
          </Button>
        </Form>
        <Table
          rowKey="id"
          columns={keyColumns}
          dataSource={keys}
          pagination={false}
          loading={keysQuery.isLoading}
          style={{ marginBottom: 24 }}
        />
        <Card title="테스트 호출">
          <Paragraph>
            방금 발급한 키가 아래 칸에 들어갑니다. 예전에 발급한 키는 원문을 직접 붙여 넣어야 합니다. 호출은 어드민
            로그인 토큰이 아니라 이 키로 API 서버에 직접 나갑니다. 판매 기간의 종료일은 포함되지 않고, 파트너 목록은
            기본 2건만 요청합니다.
          </Paragraph>
          <div style={{ maxWidth: 520, marginBottom: 8 }}>
            <div style={{ fontSize: 12, marginBottom: 4 }}>API 키</div>
            <Input.Password
              value={testApiKey}
              placeholder="igk_..."
              autoComplete="off"
              onChange={(event) => setTestApiKey(event.target.value)}
            />
          </div>
          {endpoints.map((endpoint) => (
            <EndpointTester
              key={endpoint.id}
              endpoint={endpoint}
              apiKey={testApiKey}
              onCalled={() => keysQuery.refetch()}
            />
          ))}
        </Card>
      </div>
      <Modal
        title="API 키"
        open={!!issuedKey?.apiKey}
        onCancel={() => setIssuedKey(null)}
        footer={<Button onClick={() => setIssuedKey(null)}>닫기</Button>}
        destroyOnClose>
        <Paragraph>이 키는 지금만 표시됩니다. 아래 테스트 호출란에도 넣어 두었습니다.</Paragraph>
        <Paragraph copyable={{ text: issuedKey?.apiKey }} code>
          {issuedKey?.apiKey}
        </Paragraph>
      </Modal>
    </div>
  );
};

export default IntegrationApisPage;
